/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {Link, useParams, useNavigate, useLocation} from 'react-router-dom'
import {PageTitle} from '../../../core'
import {KTCard, KTCardBody, DeleteModal, Notes, Category} from '../../../components'
import {useDispatch, useSelector} from 'react-redux'
import {userTaskDelete, userTaskGetId, userTaskComplete} from '../../../redux/slices/usertask.slice'
import {KTSVG, SourceType} from '../../../helpers'
import Dropdown from 'react-bootstrap/Dropdown'
import {
  useMemoizedPageUrl,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectUserTaskData = (state) => ({
  detail: state.userTask.detail,
  isDeleted: state.userTask.isDeleted,
  error: state.userTask.error,
  completeTask: state.userTask.completeTask,
})

function Detail() {
  const [show, setShow] = useState(false)
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()
  const {detail, isDeleted, error, completeTask} = useSelector(selectUserTaskData)
  const dispatch = useDispatch()

  const pageUrl = useMemoizedPageUrl(location)

  useEffect(() => {
    dispatch(userTaskGetId(id))
  }, [dispatch, id, completeTask])

  const handleDelete = useCallback(() => dispatch(userTaskDelete({id})), [dispatch, id])

  const handleComplete = useCallback(() => dispatch(userTaskComplete({Id: id})), [dispatch, id])

  useEffect(() => {
    if (isDeleted) navigate(`/${pageUrl[1]}/${pageUrl[2]}`)
  }, [isDeleted, navigate, pageUrl])

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.USERTASK'}),
        path: '/user/task',
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl]
  )

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.USERTASK.DETAIL'})}
      </PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          {detail && (
            <>
              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <div className='d-flex flex-wrap'>
                    <div className='flex-grow-1 w-100'>
                      <div className='d-flex justify-content-between align-items-start flex-wrap mb-5 row'>
                        <div className='d-flex flex-column col-xl-7'>
                          <div className='d-flex align-items-center mb-2'>
                            <div className='text-gray-900 fs-2 fw-bold me-1'>{detail.name}</div>

                            <div className='ms-5'></div>
                          </div>

                          <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'> </div>
                        </div>

                        <div className='d-flex gap-3 align-items-center col-xl-5 justify-content-end'>
                          <Category SourceId={Number(id)} SourceTypeId={SourceType.UserTask} />
                          {detail && detail.userTaskStatusId != 3 && (
                            <button
                              className='btn btn-sm btn-success align-self-center'
                              onClick={handleComplete}
                            >
                              <KTSVG
                                path='/images/icons/duotune/general/gen019.svg'
                                className='svg-icon-2'
                              />
                              {intl.formatMessage({id: 'BUTTON.COMPLETED'})}
                            </button>
                          )}
                          <Link
                            to={`/${pageUrl[1]}/${pageUrl[2]}/edit/${id}`}
                            className='btn btn-sm btn-primary align-self-center'
                          >
                            <KTSVG
                              path='/images/icons/duotune/general/gen055.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'BUTTON.EDIT'})}
                          </Link>
                          <Dropdown autoClose='outside' align={{lg: 'end'}}>
                            <Dropdown.Toggle
                              variant='secondary'
                              size='sm'
                              className='iconless ps-3 pe-3'
                            >
                              <KTSVG
                                path='/images/icons/duotune/general/gen053.svg'
                                className='svg-icon-2'
                              />
                            </Dropdown.Toggle>

                            <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3'>
                              <Dropdown.Item
                                onClick={() => setShow(true)}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.DELETE'})}
                                </div>
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-wrap flex-stack'>
                      <div className='d-flex flex-column flex-grow-1 pe-8'>
                        <div className='d-flex flex-wrap'>
                          {detail?.assignedUserNames?.length > 0 ? (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'USERTASK.GET.ASSIGNEDUSERS'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                <span className='text-dark'>{detail.assignedUserNames.join(', ')}</span>
                              </div>
                            </div>
                          ) : detail?.userNameSurname ? (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'USERTASK.GET.USERNAMESURNAME'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                <span className='text-dark'>{detail.userNameSurname}</span>
                              </div>
                            </div>
                          ) : null}

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'USERTASK.GET.REQUESTEDUSERNAMESURNAME'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              <span className='text-dark'>{detail?.requestedUserNameSurname}</span>
                            </div>
                          </div>

                          {detail?.taskDate && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'USERTASK.GET.TASKDATE'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                {intl.formatDate(new Date(detail?.taskDate), {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </div>
                            </div>
                          )}

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'USERTASK.GET.USERTASKSTATUSNAME'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              <span className='text-dark'>{detail?.userTaskStatusName}</span>
                            </div>
                          </div>

                          {detail?.completeDate && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'USERTASK.GET.COMPLETEDATE'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                {intl.formatDate(new Date(detail?.completeDate), {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </div>
                            </div>
                          )}

                          {detail?.taskPriorityName && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'USERTASK.GET.TASKPRIORITYID'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                <span className='text-dark'>{detail?.taskPriorityName}</span>
                              </div>
                            </div>
                          )}

                          {detail?.userTaskAuthorizationTypeName && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'USERTASK.GET.AUTHORIZATIONTYPENAME'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                <span className='text-dark'>{detail?.userTaskAuthorizationTypeName}</span>
                              </div>
                            </div>
                          )}

                          {detail?.projectName && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'USERTASK.CREATE.PROJECTID'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                <span className='text-dark'>{detail?.projectName}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </>
          )}
        </div>

        <div className='col-xl-3'>
          <Notes SourceId={Number(id)} SourceTypeId={SourceType.UserTask} />
        </div>
      </div>

      <DeleteModal
        onClick={handleDelete}
        onOpen={show}
        onClose={() => setShow(false)}
        error={error}
      />
    </>
  )
}
export {Detail}
